import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { GroupRoleApis } from 'apis/GroupRoleApis';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import EditRewardIcon from 'components/Icons/EditRewardIcon';
import ExportIcon from 'components/Icons/ExportIcon';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useDefaultFilterReward from 'hooks/useDefaultFilterReward';
import useQueryParam from 'hooks/useQueryPrams';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import {
  IMPORT_EDIT_MANAGEMENT_GROUP,
  IMPORT_GROUP_ROLE,
} from 'utils/constants/importId';
import FilterGroupRole from './FilterGroupRole';
import TableGroupRole from './TableGroupRole';

const IMPORT_EDIT = 1;
const IMPORT_CREATE = 2;
const NO_ACTION = 0;

const GroupRolePage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();

  const childRef = useRef<any>(null);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [formParams, setFormParams] = useState<any>({});

  const [formTable] = useForm();
  // const [isAdd, setIsAdd] = useState<boolean>(false);
  const [dataTable, setDataTable] = useState<any>({});
  const [indexHandle, setIndexHandle] = useState<number>(-1);
  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false);

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;
  const [importType, setImportType] = useState<number>(NO_ACTION);

  const defaultFilter = useDefaultFilterReward();

  const mutateGetGroupRole = useMutation({
    mutationFn: GroupRoleApis.getGroupRole,
    onSuccess: (res, variables) => {
      // console.log(res);
      const result = res?.data?.getGroupRoleManagement || {};
      if (variables.type === 'export') {
        exportExcel_v2(
          variables.headerRow,
          result.items || [],
          'Nhóm và chức vụ quản lý',
        );
      } else {
        setDataTable(result);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutateUpdateGroupRoleManagement = useMutation({
    mutationFn: GroupRoleApis.updateGroupRoleManagement,
    onSuccess: (res) => {
      if (res.errors?.length > 0) {
        toastError(res.errors[0].message);
        return;
      }

      toastSuccess('Cập nhật nhóm và chức vụ quản lý thành công');
      refetchGetGroupRole();
      handleCancel();
    },
    onError: (error) => {
      toastError(error.message || 'Cập nhật nhóm và chức vụ quản lý thất bại');
    },
  });

  const refetchGetGroupRole = useCallback(
    async (isExport?: boolean, headerRow?: any) => {
      try {
        await mutateGetGroupRole.mutateAsync({
          page: isExport ? 1 : page,
          pageSize: isExport
            ? dataTable?.pageInfo?.totalItems || 1000
            : page_size,
          ...(defaultFilter && {
            ...defaultFilter,
            plantId: defaultFilter?.plantId
              ? defaultFilter?.plantId.replace('all', '')
              : undefined,
          }),
          ...(formParams && formParams),
          ...(isExport && {
            headerRow,
            type: 'export',
          }),
        });
      } catch (error) {
        console.log('mutateGetGroupRole', error);
      }
    },
    [
      page,
      page_size,
      formParams,
      defaultFilter,
      dataTable?.pageInfo?.totalItems,
    ],
  );

  useEffect(() => {
    // console.log('user', user);
    refetchGetGroupRole();
  }, [refetchGetGroupRole]);

  useEffect(() => {
    setTitle('Nhóm và chức vụ quản lý');
    setBreadcrumb([
      { title: 'Doanh thu/ Thưởng' },
      { title: 'Nhóm và chức vụ quản lý' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const handleExportExcel = () => {
    if (childRef.current) {
      childRef.current.exportExcel();
    }
  };

  const handleImport = (type: number) => {
    // console.log('import');
    setIsOpenImportModal(true);
    setImportType(type);
  };

  const handleOkFilter = useCallback(
    (values: any) => {
      navigate(`?page=1&page_size=${page_size}`);
      setFormParams({
        ...values,
        plantId: values?.plantId
          ? values?.plantId.replace('all', '')
          : undefined,
      });
    },
    [page_size],
  );

  const handleCancelFilter = useCallback(() => {
    // console.log('Cancel Filter');
    setFormParams({});
  }, []);

  const handleCancel = () => {
    // setIsAdd(false);
    setIndexHandle(-1);
    formTable.resetFields();
  };

  const handleAddRow = () => {
    // setIsAdd(true);
    setIndexHandle(0);
    setDataTable((pre: any) => ({
      ...pre,
      items: [{ id: Date.now() }].concat(pre.items),
    }));
  };

  const handleCancelTable = () => {
    // if (isAdd) {
    //   setDataTable((pre: any) => ({
    //     ...pre,
    //     items: (pre?.items || []).slice(1),
    //   }));
    // }
    handleCancel();
  };

  const handleSaveTable = () => {
    const values = formTable.getFieldsValue();
    delete values['tjtobTitle'];

    mutateUpdateGroupRoleManagement.mutate({
      ...values,
      id: dataTable.items[indexHandle].id,
      status: values.status === 'true',
      rewardStatus: values.rewardStatus === 'true',
    });
  };

  const handleEditRow = (index: number) => {
    setIndexHandle(index);
    // setIsAdd(false);
    const row = dataTable.items[index];
    formTable.setFieldsValue({
      ...row,
      status: row.status ? 'true' : 'false',
      rewardStatus: row.rewardStatus ? 'true' : 'false',
    });
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            isOpen={isOpenFilter}
            setIsOpen={setIsOpenFilter}
            disabled={
              // isAdd ||
              indexHandle !== -1
            }
          />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            disabled={
              // isAdd ||
              indexHandle !== -1
            }
          />
        </Col>
        <Col>
          <Button
            title="Import Tạo mới"
            iconBefore={<ExportIcon />}
            handleOnclick={() => handleImport(IMPORT_CREATE)}
            disabled={
              // isAdd ||
              indexHandle !== -1 || !(user?.isAdmin || user?.isHO || user?.isNS)
            }
          />
        </Col>
        <Col>
          <Button
            title="Import Chỉnh sửa"
            iconBefore={<EditRewardIcon />}
            handleOnclick={() => handleImport(IMPORT_EDIT)}
            disabled={
              // isAdd ||
              indexHandle !== -1 || !(user?.isAdmin || user?.isHO || user?.isNS)
            }
          />
        </Col>
      </Row>
      <FilterGroupRole
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />
      <TableGroupRole
        ref={childRef}
        form={formTable}
        data={dataTable}
        indexHandle={indexHandle}
        onCancel={handleCancelTable}
        onEdit={handleEditRow}
        onSave={handleSaveTable}
        loading={mutateGetGroupRole.isPending}
        refetch={refetchGetGroupRole}
      />

      <UploadModal
        // popConfirm={
        //   importType === IMPORT_EDIT
        //     ? {
        //         title: 'Chỉnh sửa nhóm và chức vụ quản lý',
        //         description: 'Bạn có chắc chắn muốn chỉnh sửa không?',
        //         okText: 'Chỉnh sửa',
        //         cancelText: 'Hủy',
        //       }
        //     : undefined
        // }
        importId={
          importType === IMPORT_CREATE
            ? IMPORT_GROUP_ROLE
            : IMPORT_EDIT_MANAGEMENT_GROUP
        }
        templateFileName="Nhóm và chức vụ quản lý"
        open={isOpenImportModal}
        title={
          importType === IMPORT_CREATE
            ? 'Thêm nhóm và quản lý chức vụ mới'
            : 'Chỉnh sửa nhóm và quản lý chức vụ'
        }
        setOpen={setIsOpenImportModal}
        refetch={refetchGetGroupRole}
      />
    </>
  );
};

export default GroupRolePage;
